import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "IndiaODR";
const DEFAULT_DESC =
  "IndiaODR — Global Arbitration & ODR Intelligence. AI-assisted, human-reviewed legal news on international arbitration, Indian disputes, online dispute resolution, award enforcement and institutional updates.";

/**
 * Per-route SEO. Drop <Seo .../> at the top of any page.
 *
 *   title:        page-specific title (will be suffixed with " — IndiaODR")
 *   description:  meta description + OG description
 *   path:         current URL path used to build absolute canonical & og:url
 *   image:        absolute or relative; falls back to /og-default.png
 *   type:         OG type — "website" | "article" | "profile" (default "website")
 *   noIndex:      set to true for /admin/* and 404
 *   jsonLd:       optional JSON-LD object(s) — array or single
 *   prev / next:  pagination URLs (path or absolute)
 */
export default function Seo({
  title,
  description = DEFAULT_DESC,
  path = "",
  image,
  type = "website",
  noIndex = false,
  jsonLd,
  prev,
  next,
}) {
  const siteOrigin =
    (typeof window !== "undefined" && window.location.origin) ||
    process.env.REACT_APP_BACKEND_URL ||
    "";
  const canonical = `${siteOrigin}${path || ""}`;
  const ogImage = image
    ? (image.startsWith("http") ? image : `${siteOrigin}${image}`)
    : `${siteOrigin}/og-default.png`;
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Global Arbitration & ODR Intelligence`;
  const robots = noIndex
    ? "noindex, nofollow"
    : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";

  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  // GSC + Bing verification — only injected if env values exist
  const gsc = process.env.REACT_APP_GSC_VERIFICATION;
  const bing = process.env.REACT_APP_BING_VERIFICATION;

  const absPrev = prev ? (prev.startsWith("http") ? prev : `${siteOrigin}${prev}`) : null;
  const absNext = next ? (next.startsWith("http") ? next : `${siteOrigin}${next}`) : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {absPrev && <link rel="prev" href={absPrev} />}
      {absNext && <link rel="next" href={absNext} />}

      {gsc && <meta name="google-site-verification" content={gsc} />}
      {bing && <meta name="msvalidate.01" content={bing} />}

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Build common JSON-LD structures used across the site.
 */
export const ld = {
  organization: (origin) => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: origin,
    logo: `${origin}/og-default.png`,
    sameAs: [],
  }),

  website: (origin) => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: origin,
    potentialAction: {
      "@type": "SearchAction",
      target: `${origin}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  }),

  breadcrumb: (origin, items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.path ? `${origin}${it.path}` : undefined,
    })),
  }),

  person: (origin, author) => ({
    "@context": "https://schema.org",
    "@type": "Person",
    name: author.name,
    description: author.bio,
    jobTitle: author.role,
    url: `${origin}/author/${author.slug}`,
  }),

  collectionPage: (origin, name, path, description) => ({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: `${origin}${path}`,
  }),
};
